import { useMemo, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { LandingFooter } from '@/features/landing/components/LandingFooter';
import { LandingHeader } from '@/features/landing/components/LandingHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * Moldura das duas telas legais (Termos de Uso, Política de Privacidade).
 *
 * Cabeçalho/rodapé da landing, de propósito: são páginas públicas que qualquer
 * visitante pode abrir sem sessão, no mesmo "site" institucional — não fazem
 * parte do app autenticado (`AppLayout`) nem do fluxo de auth (`AuthShell`).
 *
 * O corpo é Markdown puro (`conteudo/*.md`, importado via `?raw`) — texto
 * jurídico longo é a única razão desta tela existir. O layout imita o documento
 * de referência em PDF: capa com metadados, índice de seções gerado das linhas
 * `## `, tipografia de "chapter" e caixas de destaque para os rótulos `#### `.
 */

/** "1. Definições Aplicáveis" -> "1-definicoes-aplicaveis" (para âncora e índice). */
function slug(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove marcas de acento
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Achata os filhos de um heading do react-markdown numa string. */
function textoDe(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textoDe).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    return textoDe((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

export interface MetadadoLegal {
  rotulo: string;
  valor: string;
}

export function LegalLayout({
  titulo,
  descricao,
  metadados,
  entidade,
  markdown,
}: {
  titulo: string;
  /** Frase curta que resume o documento (linha de abertura, abaixo do título). */
  descricao: ReactNode;
  /** Vigência, versão, última atualização — renderizados numa grade na capa. */
  metadados?: MetadadoLegal[];
  /** Razão social + identificação da Controladora, em texto miúdo na capa. */
  entidade?: ReactNode;
  markdown: string;
}) {
  const secoes = useMemo(
    () =>
      [...markdown.matchAll(/^##\s+(.+?)\s*$/gm)]
        .map((m) => (m[1] ?? '').trim())
        .filter(Boolean)
        // "Referências Legais" e afins não recebem número — ainda assim entram no índice.
        .map((t) => ({ titulo: t, id: slug(t) })),
    [markdown],
  );

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />

      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
        {/* ── Capa ──────────────────────────────────────────────────────── */}
        <header className="border-b border-border pb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Documento jurídico da plataforma
          </p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground text-balance sm:text-4xl">
            {titulo}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{descricao}</p>

          {metadados && metadados.length > 0 && (
            <dl className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2">
              {metadados.map((m) => (
                <div key={m.rotulo} className="bg-surface-raised px-4 py-3">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {m.rotulo}
                  </dt>
                  <dd className="mt-0.5 text-sm font-medium text-foreground">{m.valor}</dd>
                </div>
              ))}
            </dl>
          )}

          {entidade && (
            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">{entidade}</p>
          )}
        </header>

        {/* ── Índice ────────────────────────────────────────────────────── */}
        {secoes.length > 1 && (
          <nav
            aria-label="Índice do documento"
            className="mt-10 rounded-lg border border-border bg-brand-muted/40 p-5 print:hidden"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">Índice</p>
            <ol className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
              {secoes.map((s) => (
                <li key={s.id} className="text-sm leading-snug">
                  <a
                    href={`#${s.id}`}
                    className="text-muted-foreground underline-offset-2 transition-colors hover:text-brand hover:underline"
                  >
                    {s.titulo}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* ── Corpo ─────────────────────────────────────────────────────── */}
        <div className="mt-12">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => (
                <h2
                  id={slug(textoDe(children))}
                  className="mt-14 scroll-mt-28 border-b border-border pb-2 text-xl font-bold tracking-tight text-brand first:mt-0 sm:text-2xl"
                >
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-8 scroll-mt-28 text-base font-semibold text-foreground sm:text-lg">
                  {children}
                </h3>
              ),
              // Rótulo `#### ` = caixa de destaque (equivale aos boxes do PDF:
              // "Aceitação", "Exceção Expressa", "Finalidades Proibidas"...).
              h4: ({ children }) => (
                <p className="mt-8 border-l-4 border-brand bg-brand-muted/60 px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand">
                  {children}
                </p>
              ),
              p: ({ children }) => (
                <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground first:mt-0">
                  {children}
                </p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">{children}</strong>
              ),
              em: ({ children }) => <em className="italic text-foreground/80">{children}</em>,
              a: ({ href, children }) => (
                <a
                  href={href}
                  target={href?.startsWith('#') ? undefined : '_blank'}
                  rel={href?.startsWith('#') ? undefined : 'noreferrer'}
                  className="text-brand underline underline-offset-2 hover:text-brand/80"
                >
                  {children}
                </a>
              ),
              ul: ({ children }) => (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground marker:text-brand/50">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="mt-4 list-decimal space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground marker:text-brand/60 marker:font-medium">
                  {children}
                </ol>
              ),
              li: ({ children }) => <li className="pl-1">{children}</li>,
              hr: () => <hr className="my-10 border-border" />,
              blockquote: ({ children }) => (
                <blockquote className="mt-4 border-l-4 border-brand/40 bg-muted/40 py-1 pl-4 text-[15px] italic leading-relaxed text-muted-foreground">
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <div className="mt-5 overflow-x-auto rounded-lg border border-border">
                  <Table className="text-[13px]">{children}</Table>
                </div>
              ),
              thead: ({ children }) => (
                <TableHeader className="bg-muted/60">{children}</TableHeader>
              ),
              tbody: ({ children }) => <TableBody>{children}</TableBody>,
              tr: ({ children }) => (
                <TableRow className="even:bg-muted/20">{children}</TableRow>
              ),
              th: ({ children }) => (
                <TableHead className="whitespace-normal align-top text-xs font-semibold uppercase tracking-wide text-foreground">
                  {children}
                </TableHead>
              ),
              td: ({ children }) => (
                <TableCell className="whitespace-normal align-top leading-relaxed text-muted-foreground">
                  {children}
                </TableCell>
              ),
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
